import React, { useMemo } from "react";
import { useDispatch } from "react-redux";

import { IgDivider, IgTypography } from "@intelligems/ig-theme";
import {
  GenericExperienceFe,
  OrganizationDto,
  ScheduleDto,
} from "@intelligems/ig-types";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useCanStartExperience } from "@src/components/LimitAccess/experiment-scope-hooks";
import {
  getExperienceStatusDate,
  getExperienceStatusText,
} from "@src/features/experiments/utils/utils";
import { setDialog } from "@src/store/slices/global";
import {
  fullDateTimeConverter,
  getExperienceRunTime,
} from "@src/utils/dateConverters";

interface ExperienceDurationTooltipProps {
  experience: GenericExperienceFe;
  schedules: ScheduleDto[];
  organization: OrganizationDto;
}

export const ExperienceDurationTooltip = ({
  experience,
  schedules,
  organization,
}: ExperienceDurationTooltipProps) => {
  const dispatch = useDispatch();
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const { canStart } = useCanStartExperience(experience);

  const statusSection = useMemo(() => {
    const statusText = getExperienceStatusText(experience);
    const statusDate = getExperienceStatusDate(experience);

    return (
      <IgTypography>
        {statusText}{" "}
        {fullDateTimeConverter(
          statusDate,
          organization?.timezone ?? undefined,
          storeCurrencyConfig?.value?.locale
        )}
        <br />
        {experience.status !== "pending" && (
          <>
            {getExperienceRunTime(
              experience.status,
              experience.experienceActions,
              organization?.timezone,
              experience.category
            )}
            <br />
          </>
        )}
      </IgTypography>
    );
  }, [experience]);

  const scheduleSection = useMemo(() => {
    const startAtSchedule = schedules.find(
      (schedule) => schedule.action === "start"
    );
    const pauseAtSchedule = schedules.find(
      (schedule) => schedule.action === "pause"
    );
    const stopAtSchedule = schedules.find(
      (schedule) => schedule.action === "end"
    );

    return (
      <IgTypography>
        SCHEDULE
        {startAtSchedule && (
          <>
            <br />
            Start:{" "}
            {fullDateTimeConverter(
              startAtSchedule?.executionTime,
              organization?.timezone ?? undefined,
              storeCurrencyConfig?.value?.locale
            )}
          </>
        )}
        {pauseAtSchedule && (
          <>
            <br />
            Pause:{" "}
            {fullDateTimeConverter(
              pauseAtSchedule?.executionTime,
              organization?.timezone ?? undefined,
              storeCurrencyConfig?.value?.locale
            )}
          </>
        )}
        {stopAtSchedule && (
          <>
            <br />
            Stop:{" "}
            {fullDateTimeConverter(
              stopAtSchedule?.executionTime,
              organization?.timezone ?? undefined,
              storeCurrencyConfig?.value?.locale
            )}
          </>
        )}
        <br />
        <br />
        {canStart ? (
          <IgTypography
            onClick={() => {
              dispatch(
                setDialog({
                  dialog: "scheduleTest",
                  open: true,
                  props: {
                    experience,
                  },
                })
              );
            }}
            sx={{
              textDecoration: "underline",
              "&:hover": { cursor: "pointer" },
            }}
          >
            Edit Schedule
          </IgTypography>
        ) : null}
      </IgTypography>
    );
  }, [schedules, experience]);

  const experienceDuration = useMemo(() => {
    if (schedules.length > 0) {
      return (
        <>
          {statusSection}
          <br />
          <IgDivider
            sx={{
              marginLeft: "-8px",
              marginRight: "-8px",
              backgroundColor: "#BBBB",
            }}
            variant={"fullWidth"}
          />
          <br />
          {scheduleSection}
        </>
      );
    }

    return (
      <>
        {statusSection}
        {experience.status !== "ended" && canStart ? (
          <>
            <br />
            <IgTypography
              onClick={(e) => {
                e.stopPropagation();
                dispatch(
                  setDialog({
                    dialog: "scheduleTest",
                    open: true,
                    props: {
                      experience,
                    },
                  })
                );
              }}
              sx={{
                textDecoration: "underline",
                "&:hover": { cursor: "pointer" },
              }}
            >
              Set Schedule
            </IgTypography>
          </>
        ) : null}
      </>
    );
  }, [schedules, statusSection, scheduleSection, canStart]);

  return experienceDuration;
};
